import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type NotificationBatchJSON = z.infer<typeof NotificationBatchSchema.NotificationBatchJSONZ>;

/**
 * The envelope of the `notifications-bulk-updated` realtime event (GitHub issue #2963): everything a
 * single bulk command — a screenful marked seen, a group archived — changed for this subscriber, in
 * one message.
 *
 * Only the envelope is validated here. Each entry is parsed by the same
 * {@link NotificationSchema}/{@link MessageNotificationSchema} pair that parses a single-notification
 * event, so one unreadable entry costs that entry and not the whole batch, and there is exactly one
 * place that decides what a notification payload means.
 *
 * The event name is deliberately not the plural of `notification-updated`: the two carry different
 * payload shapes, and a subscriber must not be able to confuse them by misreading one character.
 */
export default class NotificationBatchSchema {
  public static readonly NotificationBatchJSONZ = z.object({
    notifications: z.array(z.unknown()),
  });

  public static validateJSON(values: unknown): NotificationBatchJSON {
    return parseOrThrow(NotificationBatchSchema.NotificationBatchJSONZ, values, {
      logError: true,
      schemaName: "NotificationBatchSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is NotificationBatchJSON {
    try {
      NotificationBatchSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }
}
